import _ from 'lodash';

/**
 * FastAPI / Pydantic style: `{ detail: [{ loc, msg, type }, ...] }`.
 * @returns {string|null}
 */
export function formatValidationErrorsFromBody(body) {
  if (Array.isArray(body)) {
    return formatValidationErrorsFromBody({ detail: body });
  }
  if (body == null || typeof body !== "object") return null;
  const detail = body.detail ?? body.errors;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter((x) => x != null && String(x).trim() !== "").join(" → ")
            : "";
          const msg = item.ctx?.error || item.msg || item.message || "";
          if (loc && msg) return `${loc}: ${msg}`;
          return msg || JSON.stringify(item);
        }
        return String(item);
      })
      .filter(Boolean);
    return parts.length ? parts.join("; ") : null;
  }
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  if (typeof body.message === "string" && body.message.trim() !== "") {
    return body.message;
  }
  return null;
}

export function parseServerError(serverErr) {
  if (serverErr == null) return null;
  if (typeof serverErr === 'string') return serverErr;
  let error = null;
  try {
    if (_.isObject(serverErr)) {
      error =
        serverErr.error ||
        serverErr.message ||
        serverErr.detail ||
        serverErr.errors;
      if (error && typeof error === 'object' && Array.isArray(error.__all__)) {
        error = error.__all__[0];
      }
    } else {
      const errorObj = JSON.parse(serverErr);
      error = errorObj.errors?.__all__?.[0] ?? errorObj.error ?? errorObj.detail;
    }
  } catch (err) {
    error = serverErr;
  }
  return typeof error === 'string' ? error : error ?? null;
}

export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const data =
    error?.response?.data ??
    (typeof error?.payload === "string" || Array.isArray(error?.payload) || (error?.payload != null && typeof error.payload === "object")
      ? error.payload
      : undefined) ??
    error?.data ??
    error;

  if (Array.isArray(data)) {
    const first = data[0];
    if (first != null && typeof first === "object") {
      if (typeof first.ctx?.error === "string" && first.ctx.error.trim() !== "") {
        return first.ctx.error.trim();
      }
      if (typeof first.msg === "string" && first.msg.trim() !== "") {
        return first.msg.trim();
      }
      if (typeof first.message === "string" && first.message.trim() !== "") {
        return first.message.trim();
      }
    }
    if (typeof first === "string" && first.trim() !== "") {
      return first.trim();
    }
    return fallback;
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed !== "" ? trimmed : fallback;
  }

  if (data != null && typeof data === "object") {
    const fromValidation = formatValidationErrorsFromBody(data);
    if (fromValidation) {
      return fromValidation;
    }
    if (typeof data.detail === "string" && data.detail.trim() !== "") {
      return data.detail.trim();
    }
    if (Array.isArray(data.detail)) {
      return getApiErrorMessage({ response: { data: data.detail } }, fallback);
    }
    if (typeof data.message === "string" && data.message.trim() !== "") {
      return data.message.trim();
    }
    if (typeof data.error === "string" && data.error.trim() !== "") {
      return data.error.trim();
    }
  }

  if (typeof error?.payload === "string" && error.payload.trim() !== "") {
    return error.payload.trim();
  }

  if (error instanceof Error && typeof error.message === "string" && error.message.trim() !== "") {
    return error.message.trim();
  }

  return fallback;
}
